import { FC, useEffect, useMemo, useState } from "react";
import { SubscriptionsWidgetConfig } from "@/models/dashboard/WidgetConfig";
import useUsers from "@/hooks/useUsers";
import { useItemService } from "@/hooks/useServices";
import {
  buildFullDisplayOrderForGlobalUpdate,
  resolveItemOrder,
  usesWidgetItemOrder,
} from "../lib/resolveItemOrder";
import UserDataOrderSection from "./UserDataOrderSection";
import { SubscriptionCategory } from "@/models/subscriptions/Subscription";

interface SubscriptionsConfigFormProps {
  config: SubscriptionsWidgetConfig;
  onChange: (config: SubscriptionsWidgetConfig) => void;
}

const formatCategory = (category: SubscriptionCategory) => {
  switch (category) {
    case SubscriptionCategory.Legislation:
      return "Kilder";
    case SubscriptionCategory.Workspace:
      return "Workspace";
    case SubscriptionCategory.WorkspaceDocument:
      return "Dokument";
    case SubscriptionCategory.Conversation:
      return "Samtale";
    default:
      return category;
  }
};

const INITIAL_SUBSCRIPTIONS_PAGE_SIZE = 500;

const SubscriptionsConfigForm: FC<SubscriptionsConfigFormProps> = ({
  config,
  onChange,
}) => {
  const { useQueryDetailedSubscriptions, reorderSubscriptions } = useUsers();
  const userService = useItemService((services) => services.user);
  const [listPageSize, setListPageSize] = useState(INITIAL_SUBSCRIPTIONS_PAGE_SIZE);
  const subscriptionsQuery = useQueryDetailedSubscriptions(1, listPageSize);

  useEffect(() => {
    if (!subscriptionsQuery.data) {
      return;
    }

    const totalRecords =
      subscriptionsQuery.data.totalRecords ??
      subscriptionsQuery.data.totalCount ??
      subscriptionsQuery.data.data.length;
    if (totalRecords > listPageSize) {
      setListPageSize(totalRecords);
    }
  }, [subscriptionsQuery.data, listPageSize]);

  const useGlobalOrder = !usesWidgetItemOrder(
    config.useGlobalOrder,
    config.itemOrder
  );
  const displayOrder = useMemo(
    () => subscriptionsQuery.data?.displayOrder ?? [],
    [subscriptionsQuery.data?.displayOrder]
  );

  const displayItems = useMemo(() => {
    if (!subscriptionsQuery.data) return [];

    const ordered = resolveItemOrder(subscriptionsQuery.data.data, {
      globalOrder: displayOrder,
      itemOrder: config.itemOrder,
      useGlobalOrder: config.useGlobalOrder,
    });

    return ordered.map((sub) => ({
      id: sub.id,
      label: sub.entityName ?? "Ukendt",
      sublabel: formatCategory(sub.entityType),
    }));
  }, [subscriptionsQuery.data, displayOrder, config.itemOrder, config.useGlobalOrder]);

  const currentOrderedIds = useMemo(() => {
    if (!useGlobalOrder && config.itemOrder && config.itemOrder.length > 0) {
      return config.itemOrder;
    }
    return displayItems.map((item) => item.id);
  }, [useGlobalOrder, config.itemOrder, displayItems]);

  const updateConfig = (updates: Partial<SubscriptionsWidgetConfig>) => {
    onChange({ ...config, ...updates });
  };

  const handleUseGlobalOrderChange = (useGlobal: boolean) => {
    if (useGlobal) {
      updateConfig({ useGlobalOrder: true, itemOrder: undefined });
    } else {
      updateConfig({
        useGlobalOrder: false,
        itemOrder: currentOrderedIds,
      });
    }
  };

  const handleItemOrderChange = (orderedIds: string[]) => {
    updateConfig({ useGlobalOrder: false, itemOrder: orderedIds });
  };

  const handleUpdateGlobalOrder = async () => {
    if (!subscriptionsQuery.data) return;

    const totalRecords =
      subscriptionsQuery.data.totalRecords ??
      subscriptionsQuery.data.totalCount ??
      subscriptionsQuery.data.data.length;

    let allIds: string[];
    if (
      totalRecords > 0 &&
      subscriptionsQuery.data.data.length >= totalRecords
    ) {
      allIds = subscriptionsQuery.data.data.map((sub) => sub.id);
    } else {
      const allResponse = await userService.getDetailedSubscriptions(
        1,
        Math.max(totalRecords, 1)
      );
      if (!allResponse.success) return;
      allIds = allResponse.value.data.map((sub) => sub.id);
    }
    const ids = buildFullDisplayOrderForGlobalUpdate(
      currentOrderedIds,
      allIds,
      displayOrder
    );
    await reorderSubscriptions.mutateAsync(ids);
    updateConfig({ useGlobalOrder: true, itemOrder: undefined });
  };

  return (
    <div className="space-y-6">
      <UserDataOrderSection
        useGlobalOrder={useGlobalOrder}
        items={displayItems}
        droppableId="subscriptions-config-order"
        onUseGlobalOrderChange={handleUseGlobalOrderChange}
        onItemOrderChange={handleItemOrderChange}
        onUpdateGlobalOrder={handleUpdateGlobalOrder}
        isUpdatingGlobal={reorderSubscriptions.isPending}
      />
    </div>
  );
};

export default SubscriptionsConfigForm;
